// "Watch something new": the subject in the reader's own words + a cadence.
// The chat beside it does the same job in prose ("watch whether October
// picks up, every 48 hours"); this is the typed fast path.
import { useState } from 'react';
import { CADENCE_OPTIONS } from '../../pages/merlinWatchesUtils';
import type { WatchCadence } from '../../services/merlinWatches';
import styles from './watches.module.css';

export interface WatchNewFormProps {
  busy: boolean;
  onCreate: (subject: string, cadence: WatchCadence) => Promise<unknown>;
  /** Mention that the chat can do it too (the Briefing page). */
  chatHint?: boolean;
}

export function WatchNewForm({ busy, onCreate, chatHint = false }: WatchNewFormProps) {
  const [subject, setSubject] = useState('');
  const [cadence, setCadence] = useState<WatchCadence>('weekly');

  const submit = async () => {
    const created = await onCreate(subject, cadence);
    if (created) setSubject('');
  };

  return (
    <div className={styles.newForm} data-testid="watch-new-form">
      <h2>Watch something new</h2>
      <p className={styles.hint}>
        Keep it inside what the task forces are for: a Delta cost project and whether it feels
        slow, whether October is picking up on the sales site, whether our quality responses
        carry the personal touch.
        {chatHint ? ' Or just tell Merlin in the chat.' : ''}
      </p>
      <label className={styles.label} htmlFor="watch-subject">
        What should Merlin watch?
      </label>
      <textarea
        id="watch-subject"
        className={styles.textarea}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="e.g. Are our quality responses carrying the personal touch we agreed on?"
        maxLength={2000}
        rows={4}
      />
      <div className={styles.formRow}>
        <label className={styles.label} htmlFor="watch-cadence">
          How often
        </label>
        <select
          id="watch-cadence"
          className={styles.select}
          value={cadence}
          onChange={(e) => setCadence(e.target.value as WatchCadence)}
          data-action="watch_cadence_pick"
          data-record-value="cadence"
          data-outcome="none"
        >
          {CADENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => void submit()}
          disabled={busy || subject.trim().length < 3}
          data-action="watch_create"
          data-outcome="request"
        >
          {busy ? 'Working…' : 'Start watching'}
        </button>
      </div>
      <p className={styles.hint}>
        Merlin writes the first readout straight away, then again at the cadence you chose.
      </p>
    </div>
  );
}
